/**
 * History of engineering calculations.
 *
 * Each calculation keeps its OWN record, with its own identifier sequence and
 * its own norm — this record carries no formula and never merges results from
 * different calculators.
 */

export type CalculationKind = "API570" | "NACE" | "ASME_B313";

export const CALCULATION_KINDS: readonly CalculationKind[] = ["API570", "NACE", "ASME_B313"] as const;

/** Identifier prefix per calculation: CALC-API-0001, CALC-NACE-0001, CALC-B313-0001. */
export const CALCULATION_PREFIXES: Record<CalculationKind, string> = {
  API570: "CALC-API",
  NACE: "CALC-NACE",
  ASME_B313: "CALC-B313"
};

export const CALCULATION_LABELS: Record<CalculationKind, string> = {
  API570: "API 570 — Taxa de Corrosão e Vida Remanescente",
  NACE: "NACE / AMPP — Cupom de Corrosão",
  ASME_B313: "ASME B31.3 — Espessura Mínima de Parede"
};

/** Norm each calculator answers to. Recorded with every result. */
export const CALCULATION_NORMS: Record<CalculationKind, { norm: string; edition: string }> = {
  API570: { norm: "API 570", edition: "Piping Inspection Code" },
  NACE: { norm: "NACE/AMPP RP0775", edition: "Preparation, Installation, Analysis and Interpretation of Corrosion Coupons" },
  ASME_B313: { norm: "ASME B31.3", edition: "Process Piping — para. 304.1.2" }
};

export interface CalculationRecord {
  id: string;
  /** Sequential, human-readable identifier of this calculation kind. */
  code: string;
  kind: CalculationKind;
  norm: string;
  /** Edição da norma efetivamente adotada neste cálculo, registrada junto do resultado. */
  normEdition: string;
  editionId?: string | null;
  performedAt: string;
  performedByUserId: string;
  performedByName: string;
  clientId: string | null;
  /** Linha/equipamento a que o cálculo se refere — é o que liga um ASME B31.3 à TAG. */
  equipmentId?: string | null;
  equipmentTag: string;
  notes: string;
  /** Origem de cada parâmetro: cadastro da linha, tabela normativa ou entrada manual justificada. */
  provenance?: Record<string, string>;
  inputs: Record<string, unknown>;
  result: Record<string, unknown>;
  units: Record<string, string>;
}

export function formatCalculationCode(kind: CalculationKind, sequence: number): string {
  return `${CALCULATION_PREFIXES[kind]}-${String(sequence).padStart(5, "0")}`;
}
